import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { batchesAPI } from '../services/batches';
import { financeAPI } from '../services/api';
import { LabelWithInfo } from './InfoTooltip';
import { useAuth } from '../contexts/AuthContext';

function ServiceAdjustmentDialog({ open, onClose, student, onSuccess }) {
  const [extendDays, setExtendDays] = useState('');
  const [addBonusClasses, setAddBonusClasses] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation(
    (data) => financeAPI.serviceAdjustment(student.subscription_id, data),
    {
      onSuccess: () => {
        toast.success('Subscription updated');
        onSuccess?.();
        onClose();
        setExtendDays('');
        setAddBonusClasses('');
        setReason('');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Update failed'),
    }
  );

  const handleSubmit = () => {
    const payload = { reason };
    if (extendDays) payload.extendDays = Number(extendDays);
    if (addBonusClasses) payload.addBonusClasses = Number(addBonusClasses);
    if (!payload.extendDays && !payload.addBonusClasses) {
      toast.error('Enter extend days and/or bonus classes');
      return;
    }
    mutation.mutate(payload);
  };

  if (!student?.subscription_id) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Subscription</DialogTitle>
        <DialogContent>
          <Alert severity="warning">This student has no active subscription record.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Adjust subscription — {student.student_name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
              <LabelWithInfo
                label="Extend subscription dates"
                title="Extend service period"
                body="Moves access and next due date forward. Use when the student lost class time."
              />
            </Typography>
            <TextField
              label="Extend days (optional)"
              type="number"
              size="small"
              fullWidth
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
            />
          </Box>
          <TextField
            label="Add bonus classes (optional)"
            type="number"
            size="small"
            value={addBonusClasses}
            onChange={(e) => setAddBonusClasses(e.target.value)}
          />
          <TextField
            label="Reason"
            required
            size="small"
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={mutation.isLoading || !reason.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function BatchPausePanel({ batchId, activePause: initialPause }) {
  const queryClient = useQueryClient();
  const { canManageBatches, isSales } = useAuth();
  const canPause = canManageBatches();
  const canAdjustSubs = canManageBatches() || isSales();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [adjustStudent, setAdjustStudent] = useState(null);

  const pauseQuery = useQuery(
    ['batchPause', batchId],
    async () => {
      const res = await batchesAPI.getActivePause(batchId);
      return res.data?.data;
    },
    { initialData: initialPause }
  );

  const studentsQuery = useQuery(
    ['batchPauseStudents', batchId],
    async () => {
      const res = await batchesAPI.getPauseStudents(batchId);
      return res.data?.data || [];
    },
    { enabled: !!batchId && canAdjustSubs }
  );

  const invalidateBatchAndTeacherViews = () => {
    queryClient.invalidateQueries(['batchPause', batchId]);
    queryClient.invalidateQueries(['batch', batchId]);
    queryClient.invalidateQueries(['teacherHome']);
    queryClient.invalidateQueries(['teacherHomeCalendarWeek']);
  };

  const createMutation = useMutation(
    (data) => batchesAPI.createPause(batchId, data),
    {
      onSuccess: () => {
        toast.success('Batch pause created');
        invalidateBatchAndTeacherViews();
        setStartDate('');
        setEndDate('');
        setReason('');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to pause batch'),
    }
  );

  const endMutation = useMutation(() => batchesAPI.endPause(batchId), {
    onSuccess: () => {
      toast.success('Batch pause ended');
      invalidateBatchAndTeacherViews();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to end pause'),
  });

  const activePause = pauseQuery.data;

  if (!canPause && !activePause) return null;

  return (
    <Box>
      <DividerLike />
      <Typography variant="h6" gutterBottom>
        <LabelWithInfo
          label="Batch pause"
          title="Temporary break"
          body="Stops classes for this batch between start and end dates. Only one pause can be active at a time. Does not change subscriptions automatically."
        />
      </Typography>

      {activePause ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Paused {activePause.start_date} → {activePause.end_date}
          {activePause.reason ? ` — ${activePause.reason}` : ''}
          {canPause ? (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => endMutation.mutate()}
                disabled={endMutation.isLoading}
              >
                End pause
              </Button>
            </Box>
          ) : null}
        </Alert>
      ) : canPause ? (
        <Stack spacing={2} sx={{ maxWidth: 480, mb: 2 }}>
          <TextField
            label="Pause start"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            label="Pause end"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            inputProps={{ min: startDate }}
          />
          <TextField
            label="Reason"
            size="small"
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button
            variant="contained"
            disabled={!startDate || !endDate || !reason.trim() || createMutation.isLoading}
            onClick={() =>
              createMutation.mutate({
                startDate,
                endDate,
                reason,
              })
            }
          >
            Pause batch
          </Button>
        </Stack>
      ) : null}

      {canAdjustSubs && (activePause || studentsQuery.data?.length) ? (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            <LabelWithInfo
              label="Student subscriptions"
              title="Manual billing adjustment"
              body="Pausing a batch does not change subscriptions. Adjust each student manually if needed."
            />
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Access end</TableCell>
                  <TableCell>Next due</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(studentsQuery.data || []).map((row) => (
                  <TableRow key={row.student_id}>
                    <TableCell>{row.student_name}</TableCell>
                    <TableCell>{row.access_period_end || '—'}</TableCell>
                    <TableCell>{row.next_due_date || '—'}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => setAdjustStudent(row)}>
                        Adjust
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}

      <ServiceAdjustmentDialog
        open={Boolean(adjustStudent)}
        onClose={() => setAdjustStudent(null)}
        student={adjustStudent}
        onSuccess={() => studentsQuery.refetch()}
      />
    </Box>
  );
}

function DividerLike() {
  return <Box sx={{ borderTop: 1, borderColor: 'divider', my: 3 }} />;
}
